"use client";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";

interface LabCardProps {
  title: string;
  description: string;
  href: string;
  color: "teal" | "yellow" | "pink" | "peach" | "blue";
  /** Omit for a neutral Labs placeholder tile (highlights without custom art). */
  ogImage?: string;
  /** Smaller tile — holiday cards and secondary strips (shorter media + tighter type than default). */
  variant?: "default" | "compact";
}

const colors = {
  teal: "rgb(113, 217, 197)",
  yellow: "rgb(255, 226, 138)",
  pink: "rgb(246, 187, 205)",
  peach: "rgb(255, 142, 114)",
  blue: "rgb(59, 130, 246)",
};

export default function LabCard({
  title,
  description,
  href,
  color,
  ogImage,
  variant = "default",
}: LabCardProps) {
  const isGif = ogImage?.endsWith(".gif") || ogImage?.includes(".gif");
  const compact = variant === "compact";

  return (
    <Link
      href={href || "#"}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${title} project in new window`}
      className="block bg-card-texture rounded-xl overflow-hidden"
    >
      <motion.div
        className="group relative overflow-hidden rounded-xl"
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
      >
        {/* Compact: same 16×9 media as default but tighter copy — image fills the grid column (e.g. holiday cards row). */}
        <div
          className={
            compact
              ? "relative aspect-video w-full overflow-hidden"
              : "relative aspect-video overflow-hidden"
          }
        >
          {ogImage ? (
            <Image
              src={ogImage}
              alt={title}
              width={compact ? 640 : 400}
              height={compact ? 360 : 300}
              className="h-full min-h-0 w-full object-cover"
              unoptimized={Boolean(isGif)}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-950 px-4 text-center dark:from-zinc-900 dark:via-zinc-950 dark:to-black">
              <span className="text-sm font-medium leading-snug text-white/35">
                {title}
              </span>
            </div>
          )}

          {/* Hover overlay */}
          <div
            className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-20 z-20 rounded-t-xl"
            style={{ backgroundColor: colors[color] }}
          />
        </div>

        {/* Content */}
        <div
          className={`relative rounded-b-xl backdrop-blur-[2px] ${compact ? "p-3" : "p-6"}`}
        >
          <div className={compact ? "space-y-1" : "space-y-2"}>
            <h3
              className={`font-medium ${compact ? "text-sm leading-snug sm:text-base" : "text-xl"}`}
            >
              {title}
            </h3>
            <p
              className={`text-text-muted-light dark:text-text-muted-dark ${compact ? "text-xs leading-relaxed sm:text-sm" : ""}`}
            >
              {description}
            </p>
          </div>
        </div>
      </motion.div>
    </Link>
  );
}
